import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from "react";
import {
  ADULT_AGE_MIN,
  ADULT_CONTENT_TERMS,
  DEFAULT_PRIVACY_URL,
  DEFAULT_TERMS_URL,
  OS_AGE_GATE_EVENT,
  PARENTAL_NOTICE,
  PLATFORM_AGE_MIN,
  PLATFORM_AGE_TERMS,
  acceptPlatformAgeGate,
  clearPlatformAgeDecline,
  declinePlatformAgeGate,
  hasAcceptedPlatformAgeGate,
  hasDeclinedPlatformAgeGate,
  type OsAgeGateDetail,
} from "./ageGate";

export type PlatformAgeGateProps = {
  /** When true, render nothing (e.g. already inside a gated host — rarely used). */
  disabled?: boolean;
  termsUrl?: string;
  privacyUrl?: string;
  /** Called after accept; default re-renders via state. */
  onAccepted?: () => void;
  children?: ReactNode;
};

/** Matches OsAuthSurface: landing `#hub-root` is `pointer-events: none` so constellation
 * can orbit; overlays must opt back into hits or taps fall through to WebGL. */
export const OS_AGE_GATE_ACTIVE_CLASS = "os-age-gate-active";

const overlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 2147483000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "1.25rem",
  background: "rgba(2, 8, 6, 0.92)",
  backdropFilter: "blur(8px)",
  /* Required under bonsaios.app `#hub-root { pointer-events: none }` */
  pointerEvents: "auto",
  fontFamily:
    'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  color: "#e8f0ea",
};

const cardStyle: CSSProperties = {
  width: "100%",
  maxWidth: "26rem",
  border: "1px solid rgba(120, 160, 130, 0.35)",
  background: "rgba(8, 22, 16, 0.98)",
  padding: "1.75rem 1.5rem",
  boxShadow: "none",
};

const btnBase: CSSProperties = {
  flex: 1,
  padding: "0.7rem 0.85rem",
  fontSize: "0.875rem",
  fontWeight: 600,
  cursor: "pointer",
  border: "1px solid transparent",
};

/**
 * Blocking Tier 0 gate. Renders children only after 13+ + Terms accept.
 * Under-13 shows a Safe Exit panel (no suite UI).
 */
export function PlatformAgeGate({
  disabled,
  termsUrl = DEFAULT_TERMS_URL,
  privacyUrl = DEFAULT_PRIVACY_URL,
  onAccepted,
  children,
}: PlatformAgeGateProps) {
  const [accepted, setAccepted] = useState(() =>
    disabled ? true : hasAcceptedPlatformAgeGate(),
  );
  const [declined, setDeclined] = useState(() =>
    disabled ? false : hasDeclinedPlatformAgeGate(),
  );

  useEffect(() => {
    if (disabled) return;
    const onEvt = (e: Event) => {
      const d = (e as CustomEvent<OsAgeGateDetail>).detail;
      if (!d) return;
      setAccepted(d.accepted);
      setDeclined(d.declined);
    };
    window.addEventListener(OS_AGE_GATE_EVENT, onEvt);
    return () => window.removeEventListener(OS_AGE_GATE_EVENT, onEvt);
  }, [disabled]);

  /** Landing constellation sits under `#hub-root`; mirror auth and block orbit while gated. */
  const gateBlocking = !disabled && !accepted;
  useEffect(() => {
    if (typeof document === "undefined") return;
    const html = document.documentElement;
    if (!gateBlocking) {
      html.classList.remove(OS_AGE_GATE_ACTIVE_CLASS);
      return;
    }
    html.classList.add(OS_AGE_GATE_ACTIVE_CLASS);
    return () => {
      html.classList.remove(OS_AGE_GATE_ACTIVE_CLASS);
    };
  }, [gateBlocking]);

  const onAccept = useCallback(() => {
    acceptPlatformAgeGate();
    setAccepted(true);
    setDeclined(false);
    onAccepted?.();
  }, [onAccepted]);

  const onDecline = useCallback(() => {
    declinePlatformAgeGate();
    setAccepted(false);
    setDeclined(true);
  }, []);

  const onRetry = useCallback(() => {
    clearPlatformAgeDecline();
    setDeclined(false);
  }, []);

  if (disabled || accepted) {
    return <>{children ?? null}</>;
  }

  if (declined) {
    return (
      <div style={overlayStyle} role="dialog" aria-modal aria-labelledby="os-age-exit-title">
        <div style={cardStyle}>
          <p
            style={{
              margin: "0 0 0.5rem",
              fontSize: "0.65rem",
              letterSpacing: "0.28em",
              textTransform: "uppercase",
              color: "#c4a574",
            }}
          >
            Safe exit
          </p>
          <h1
            id="os-age-exit-title"
            style={{ margin: "0 0 0.75rem", fontSize: "1.5rem", fontWeight: 600 }}
          >
            Bonsai OS is for ages {PLATFORM_AGE_MIN}+
          </h1>
          <p style={{ margin: "0 0 1rem", fontSize: "0.875rem", lineHeight: 1.55, opacity: 0.85 }}>
            {PARENTAL_NOTICE}
          </p>
          <p style={{ margin: "0 0 1.25rem", fontSize: "0.8rem", lineHeight: 1.5, opacity: 0.7 }}>
            <a href={privacyUrl} style={{ color: "#9dcca8" }}>
              Privacy Policy
            </a>
            {" · "}
            <a href={termsUrl} style={{ color: "#9dcca8" }}>
              Terms
            </a>
          </p>
          <button
            type="button"
            style={{
              ...btnBase,
              width: "100%",
              background: "transparent",
              borderColor: "rgba(120, 160, 130, 0.45)",
              color: "#e8f0ea",
            }}
            onClick={onRetry}
          >
            I am {PLATFORM_AGE_MIN}+ — show age gate again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={overlayStyle} role="dialog" aria-modal aria-labelledby="os-age-gate-title">
      <div style={cardStyle}>
        <p
          style={{
            margin: "0 0 0.5rem",
            fontSize: "0.65rem",
            letterSpacing: "0.28em",
            textTransform: "uppercase",
            color: "#c4a574",
          }}
        >
          Age · required
        </p>
        <h1
          id="os-age-gate-title"
          style={{ margin: "0 0 0.75rem", fontSize: "1.5rem", fontWeight: 600 }}
        >
          Are you over {PLATFORM_AGE_MIN}?
        </h1>
        <p
          style={{
            margin: "0 0 1.25rem",
            fontSize: "0.8rem",
            lineHeight: 1.55,
            opacity: 0.85,
            whiteSpace: "pre-wrap",
          }}
        >
          {PLATFORM_AGE_TERMS}
        </p>
        <p style={{ margin: "0 0 1.25rem", fontSize: "0.75rem", lineHeight: 1.45, opacity: 0.65 }}>
          <a href={termsUrl} style={{ color: "#9dcca8" }} target="_blank" rel="noreferrer">
            Terms of Service
          </a>
          {" · "}
          <a href={privacyUrl} style={{ color: "#9dcca8" }} target="_blank" rel="noreferrer">
            Privacy Policy
          </a>
        </p>
        <div style={{ display: "flex", gap: "0.65rem" }}>
          <button
            type="button"
            style={{
              ...btnBase,
              background: "#2d6a45",
              color: "#f4faf5",
            }}
            onClick={onAccept}
          >
            I am {PLATFORM_AGE_MIN}+ — I agree
          </button>
          <button
            type="button"
            style={{
              ...btnBase,
              background: "transparent",
              borderColor: "rgba(120, 160, 130, 0.45)",
              color: "#e8f0ea",
            }}
            onClick={onDecline}
          >
            Under {PLATFORM_AGE_MIN} — leave
          </button>
        </div>
      </div>
    </div>
  );
}

export type AdultAgeGateModalProps = {
  open: boolean;
  onAttest: () => void;
  onCancel: () => void;
  /** Override button label (default: I am 21+). */
  attestLabel?: string;
};

/** Lightweight Tier 1 modal — no II dependency; callers wire on-chain attest. */
export function AdultAgeGateModal({
  open,
  onAttest,
  onCancel,
  attestLabel = `I am ${ADULT_AGE_MIN}+`,
}: AdultAgeGateModalProps) {
  if (!open) return null;
  return (
    <div style={overlayStyle} role="dialog" aria-modal aria-labelledby="os-adult-gate-title">
      <button
        type="button"
        aria-label="Dismiss"
        style={{
          position: "absolute",
          inset: 0,
          border: "none",
          background: "transparent",
          cursor: "pointer",
        }}
        onClick={onCancel}
      />
      <div style={{ ...cardStyle, position: "relative", zIndex: 1 }}>
        <p
          style={{
            margin: "0 0 0.5rem",
            fontSize: "0.65rem",
            letterSpacing: "0.28em",
            textTransform: "uppercase",
            color: "#c4a574",
          }}
        >
          Mature · {ADULT_AGE_MIN}+
        </p>
        <h2
          id="os-adult-gate-title"
          style={{ margin: "0 0 0.75rem", fontSize: "1.35rem", fontWeight: 600 }}
        >
          Age-restricted
        </h2>
        <p
          style={{
            margin: "0 0 1.25rem",
            fontSize: "0.8rem",
            lineHeight: 1.55,
            opacity: 0.85,
            whiteSpace: "pre-wrap",
          }}
        >
          {ADULT_CONTENT_TERMS}
        </p>
        <div style={{ display: "flex", gap: "0.65rem" }}>
          <button
            type="button"
            style={{ ...btnBase, background: "#2d6a45", color: "#f4faf5" }}
            onClick={onAttest}
          >
            {attestLabel}
          </button>
          <button
            type="button"
            style={{
              ...btnBase,
              background: "transparent",
              borderColor: "rgba(120, 160, 130, 0.45)",
              color: "#e8f0ea",
            }}
            onClick={onCancel}
          >
            Leave
          </button>
        </div>
      </div>
    </div>
  );
}
